import React, { useState } from "react";
import { toast } from "sonner";
import { uploadFile, mediaUrl } from "../../../lib/blogApi";

export const inp = "w-full bg-surface border border-edge px-3 py-2 font-term text-[13px] text-coolwhite outline-none focus:border-nettred";
const lbl = "font-display tracking-[0.18em] text-[10px] uppercase text-dim";

export function Field({ label, children }) {
  return <label className="block"><span className={lbl}>{label}</span><div className="mt-1">{children}</div></label>;
}

export function Uploader({ ax, accept, onDone, testid, label = "Last opp fil" }) {
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try {
      const m = await uploadFile(ax, file, (p) => setPct(Math.round(p * 100)));
      onDone(m);
      toast.success("Lastet opp.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opplasting feilet.");
    } finally { setPct(null); }
  };
  return (
    <span className="inline-flex items-center gap-3">
      <label className="cursor-pointer font-display tracking-[0.18em] text-[11px] uppercase px-3 py-1.5 border border-edge text-coolwhite hover:border-nettred">
        {pct === null ? label : `Laster opp… ${pct}%`}
        <input data-testid={testid} type="file" accept={accept} onChange={pick} disabled={pct !== null} className="sr-only" />
      </label>
      {pct !== null && <span className="h-1.5 w-28 bg-edge"><span className="block h-full bg-l3" style={{ width: `${pct}%` }} /></span>}
    </span>
  );
}

const TYPES = { text: "Tekst", image: "Bilde", youtube: "YouTube", vimeo: "Vimeo", video: "Videofil", pdf: "PDF-slides", embed: "Slides (lenke)" };

function BlockBody({ b, set, ax, lang }) {
  const [busy, setBusy] = useState(false);
  const suggest = async () => {
    setBusy(true);
    try { const { data } = await ax.post("/admin/blog/alt-text", { media_id: b.media_id, lang }); set({ alt: data.alt }); }
    catch (e) { toast.error(e.response?.data?.detail || "AI-forslag feilet."); }
    finally { setBusy(false); }
  };
  if (b.type === "text")
    return <textarea data-testid="block-text" rows={8} className={`${inp} resize-y`} value={b.text || ""}
      placeholder={"Markdown: ## Overskrift, **fet**, *kursiv*, - liste, > sitat, [lenke](https://…)"} onChange={(e) => set({ text: e.target.value })} />;
  if (b.type === "image")
    return (
      <div className="space-y-3">
        {b.media_id ? <img src={mediaUrl(b.media_id)} alt="" className="max-h-48 border border-edge" /> :
          <Uploader ax={ax} accept="image/*" testid="block-image-upload" label="Last opp bilde (maks 15 MB)" onDone={(m) => set({ media_id: m.id })} />}
        <Field label="Alt-tekst (påkrevd)">
          <div className="flex gap-2">
            <input data-testid="block-image-alt" className={inp} value={b.alt || ""} onChange={(e) => set({ alt: e.target.value })} />
            <button data-testid="block-image-alt-ai" type="button" disabled={!b.media_id || busy} onClick={suggest}
              className="shrink-0 font-display tracking-[0.15em] text-[10px] uppercase px-3 border border-l3 text-l3 disabled:opacity-40">{busy ? "…" : "AI-forslag"}</button>
          </div>
        </Field>
        <Field label="Bildetekst (valgfri)"><input className={inp} value={b.caption || ""} onChange={(e) => set({ caption: e.target.value })} /></Field>
      </div>
    );
  if (["youtube", "vimeo", "embed"].includes(b.type))
    return (
      <div className="grid md:grid-cols-2 gap-3">
        <Field label={b.type === "embed" ? "Lenke (Google Slides / Canva / Speaker Deck)" : "Lenke"}>
          <input data-testid="block-url" className={inp} value={b.url || ""} onChange={(e) => set({ url: e.target.value })} placeholder="https://…" />
        </Field>
        <Field label="Tittel (påkrevd, leses av skjermlesere)"><input data-testid="block-title" className={inp} value={b.title || ""} onChange={(e) => set({ title: e.target.value })} /></Field>
      </div>
    );
  if (b.type === "video")
    return (
      <div className="space-y-3">
        {b.media_id ? <div className="font-term text-[12px] text-l3">✓ video lastet opp</div> :
          <Uploader ax={ax} accept="video/*" testid="block-video-upload" label="Last opp video (maks 500 MB)" onDone={(m) => set({ media_id: m.id })} />}
        <div className="flex items-center gap-3 flex-wrap">
          {b.captions_media_id ? <span className="font-term text-[12px] text-l3">✓ undertekster (.vtt)</span> :
            <Uploader ax={ax} accept=".vtt,text/vtt" testid="block-video-vtt" label="Undertekster .vtt (valgfri)" onDone={(m) => set({ captions_media_id: m.id })} />}
        </div>
        <Field label="Tittel"><input className={inp} value={b.title || ""} onChange={(e) => set({ title: e.target.value })} /></Field>
        <Field label="Transkripsjon (valgfri — ellers fra .vtt)"><textarea rows={3} className={inp} value={b.transcript || ""} onChange={(e) => set({ transcript: e.target.value })} /></Field>
      </div>
    );
  if (b.type === "pdf")
    return (
      <div className="space-y-3">
        {b.media_id ? <div className="font-term text-[12px] text-l3">✓ PDF lastet opp — teksten er hentet ut automatisk</div> :
          <Uploader ax={ax} accept="application/pdf" testid="block-pdf-upload" label="Last opp PDF (maks 50 MB)" onDone={(m) => set({ media_id: m.id, title: b.title || m.filename.replace(/\.pdf$/i, "") })} />}
        <Field label="Tittel"><input className={inp} value={b.title || ""} onChange={(e) => set({ title: e.target.value })} /></Field>
      </div>
    );
  return null;
}

export function BlockEditor({ blocks, onChange, ax, lang }) {
  const upd = (i, patch) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, d) => { const n = [...blocks]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  const btn = "font-display tracking-[0.15em] text-[10px] uppercase px-2 py-1 border border-edge text-coolwhite/80 hover:border-nettred disabled:opacity-30";
  return (
    <div className="space-y-3" data-testid="block-editor">
      {blocks.map((b, i) => (
        <div key={i} className="intel-panel p-4" style={{ "--panel-accent": "#38bdf8", borderLeftColor: "#38bdf8" }}>
          <div className="flex items-center justify-between mb-3">
            <span className="font-display tracking-[0.2em] text-[11px] uppercase text-l2">// {TYPES[b.type]}</span>
            <span className="flex gap-1">
              <button type="button" className={btn} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Flytt opp">↑</button>
              <button type="button" className={btn} disabled={i === blocks.length - 1} onClick={() => move(i, 1)} aria-label="Flytt ned">↓</button>
              <button type="button" className={btn} onClick={() => onChange(blocks.filter((_, j) => j !== i))} aria-label="Fjern blokk">✕</button>
            </span>
          </div>
          <BlockBody b={b} set={(p) => upd(i, p)} ax={ax} lang={lang} />
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        {Object.entries(TYPES).map(([k, v]) => (
          <button key={k} type="button" data-testid={`add-block-${k}`} onClick={() => onChange([...blocks, { type: k }])}
            className="font-display tracking-[0.15em] text-[10px] uppercase px-3 py-1.5 border border-edge text-coolwhite hover:border-l3 hover:text-l3">+ {v}</button>
        ))}
      </div>
    </div>
  );
}
